import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DwConfigService, DwConfigServiceToken, SuppressErrorMessagesHeader } from '@devwareapps/devware-cap';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AI_PROXY, aiSource } from '../../quizzes/components/quiz-builder-page/quiz-builder.util';
import { shuffled } from '../../quizzes/components/quiz-builder-pro-page/quiz-builder-pro.util';
import { AiSource } from '../../quizzes/models/quiz-builder.model';
import { QuestionKind, ScenarioQuestion, ScenarioStep } from '../models/scenario.model';
import { ScenarioFlowDraft, ScenarioFlowRequest, ScenarioFlowResponse } from '../models/scenario-flow-ai.model';

export interface ScenarioQuestionDraft {
  question: ScenarioQuestion;
  reference: string;
  /** TypeSafe Jev readiness, 0-1; null when the API has no TypeSafe key or the check is unavailable. */
  confidence: number | null;
  ai: AiSource;
}

/** Course, module and ACS elements the question is written for, named from CAP metadata; AI assesses one or a compatible few. */
export interface ScenarioQuestionTarget {
  course: string;
  module?: string;
  acs?: { code: string; description: string }[];
}

const NO_USABLE_QUESTION = 'The AI returned no usable question. Try again.';

/** Author-facing message for a failed draft from the CAP API or local prototype proxy. */
export function scenarioAiError(error: Partial<HttpErrorResponse>): string {
  return error.status === 403 ? 'AI drafting is available to content administrators.'
    : error.status === 401 ? 'Sign in again to draft with AI.'
    : typeof error.error?.detail === 'string' ? error.error.detail
    : typeof error.error?.error === 'string' ? error.error.error
    : 'AI drafting is unavailable. Please try again.';
}

/** AI drafts via the CAP API, or the prototype proxy when explicitly enabled on localhost. */
@Injectable({ providedIn: 'root' })
export class ScenarioQuestionAiService {
  private readonly api: string;
  private readonly flowApi: string;
  private readonly coachingApi: string;

  constructor(private readonly http: HttpClient, @Inject(DwConfigServiceToken) config: DwConfigService) {
    const host = typeof window === 'undefined' ? '' : window.location.hostname;
    const local = host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
    this.api = local && (environment as { localScenarioAiProxy?: boolean }).localScenarioAiProxy
      ? `${AI_PROXY}/scenario-question`
      : `${config.coreConfig.apiRoot}/aviator-online/ai/scenario-questions`;
    this.flowApi = this.api === `${AI_PROXY}/scenario-question` ? `${AI_PROXY}/scenario-flow` : `${this.api}/flow`;
    this.coachingApi = this.api === `${AI_PROXY}/scenario-question` ? `${AI_PROXY}/scenario-coaching` : `${this.api}/coaching`;
  }

  /** Draft only coaching; keep the authored question and its answer key unchanged. */
  draftCoaching(question: ScenarioQuestion, scenario: string): Observable<string> {
    const { imageUrl, bankQuestionId, bankSource, libraryQuestionId, points, ...content } = question;
    return this.http.post<{ text: string; model: string }>(this.coachingApi, { question: content, scenario }, {
      headers: { [SuppressErrorMessagesHeader]: 'true' },
    }).pipe(map(response => {
      const text = typeof response?.text === 'string' ? response.text.trim() : '';
      if (!text || text.length > 1200) { throw { error: { detail: 'The AI returned no usable coaching. Try again.' } }; }
      return text;
    }));
  }

  /** One coherent story; the API validates content, and the client owns ids, layout, points and connections. */
  draftFlow(request: ScenarioFlowRequest): Observable<ScenarioFlowDraft> {
    return this.http.post<ScenarioFlowResponse>(this.flowApi, request, {
      headers: { [SuppressErrorMessagesHeader]: 'true' },
    }).pipe(map(res => {
      if (!res || !res.title || !res.briefing || !res.debrief || !Array.isArray(res.questions)
        || res.questions.length !== request.formats.reduce((sum, f) => sum + f.count, 0)
        || request.formats.some(f => res.questions.filter(q => q && q.kind === f.kind).length !== f.count)
        || res.questions.some(q => (request.structure === 'guided' && !q.remediation)
          || (request.structure === 'linear' && !!q.remediation)
          || (q.remediation && (!q.remediation.title?.trim() || !q.remediation.body?.trim())))) {
        throw { error: { detail: 'The AI returned no usable flow. Try again.' } };
      }
      const prefix = `flow-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const steps: ScenarioStep[] = [
        { id: `${prefix}-0`, type: 'narrative', title: 'Briefing', body: res.briefing, x: 0, y: 100 },
      ];
      steps[0].next = `${prefix}-1`;
      res.questions.forEach((q, index) => {
        const { remediation, ...content } = q;
        const id = `${prefix}-${index + 1}`, next = `${prefix}-${index + 2}`;
        const step: ScenarioStep = { id, type: 'question', title: q.title, x: (index + 1) * 280, y: 100, next,
          question: { ...content, points: 5, ...(q.choices ? { choices: shuffled(q.choices) } : {}),
            ...(q.whys ? { whys: shuffled(q.whys) } : {}) } };
        steps.push(step);
        if (remediation) {
          step.coaching = { text: remediation.body, title: remediation.title, after: 'retry' };
        }
      });
      steps.push({ id: `${prefix}-${res.questions.length + 1}`, type: 'debrief', title: 'Debrief', body: res.debrief,
        x: (res.questions.length + 1) * 280, y: 100 });
      return { scenario: { title: res.title, subtitle: res.subtitle || '', steps }, ai: aiSource(res.model) };
    }));
  }

  /** Pass `current` to get another version of the author's question; the API keeps only its content fields. */
  draft(kind: QuestionKind, topic: string, scenario: string, points: number, current?: ScenarioQuestion,
        target?: ScenarioQuestionTarget): Observable<ScenarioQuestionDraft> {
    type Raw = { question: ScenarioQuestion; reference: string; confidence: number | null; model: string };
    // Images can be large data URLs and are never context for the text, so they stay in the browser.
    const context = current ? { ...current, imageUrl: undefined } : undefined;
    // The builder shows its own message, so the global error toast stays quiet.
    return this.http.post<Raw>(this.api, { kind, topic, scenario, target, current: context }, {
      headers: { [SuppressErrorMessagesHeader]: 'true' },
    }).pipe(
      map(res => {
        // The API validates every kind; this only guards against an out-of-date API answering for another kind.
        if (!res || !res.question || res.question.kind !== kind) {
          throw { error: { detail: NO_USABLE_QUESTION } };
        }
        const q: ScenarioQuestion = { ...res.question, points };
        // Writers tend to list the correct choice first; the answer-key flags travel with each choice.
        if (q.choices) { q.choices = shuffled(q.choices); }
        if (q.whys) { q.whys = shuffled(q.whys); }
        return { question: q, reference: res.reference || '', confidence: res.confidence ?? null, ai: aiSource(res.model) };
      })
    );
  }
}
